/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { FormulaContextLoader } from '@gh/builder-shared';
import {
  BulkUpdateRestService, ComponentRestService, CostCenterRestService, UomRestService, } from '@gh/builder-shared-data';
import {
  getRouteParam,
  getRouteQueryParam,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { AutocompleteItem, ID, withDisplayAsNameAndDescription } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  ComponentDetails,
  componentDetailsFromDto,
  componentDetailsToDto,
  newComponentDetails,
} from './component-and-item-details.model';

@Injectable()
export class ComponentDetailsBusinessService implements InitViewService {
  constructor(private componentRest: ComponentRestService,
              private uomRest: UomRestService,
              private costCenterRest: CostCenterRestService,
              private bulkUpdateRest: BulkUpdateRestService,
              private gridLayoutService: GridLayoutService,
              private formulaContextLoader: FormulaContextLoader) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const itemId = getRouteParam(route, 'componentId');
    const sourceItemId = getRouteQueryParam(route, 'cloneFrom');

    return {
      uoms: this.uomRest.findAllShared(),
      formulaContext: this.formulaContextLoader.loadForEstimating(),
      component: isRouteForCreationMode(route) ?
        (sourceItemId ? this.componentRest.clone(sourceItemId).pipe(map$(componentDetailsFromDto)) : newComponentDetails())
        : this.componentRest.get(itemId).pipe(map$(componentDetailsFromDto)),
      itemsGridLayout: this.gridLayoutService.get('componentItems'),
    };
  }

  get(id: ID): Observable<ComponentDetails> {
    return this.componentRest.get(id).pipe(map$(componentDetailsFromDto));
  }

  validate(id: ID): Observable<ComponentDetails> {
    return this.componentRest.validate(id).pipe(map$(componentDetailsFromDto));
  }

  save(component: ComponentDetails): Observable<ComponentDetails> {
    if (component.id) {
      return this.componentRest.update(componentDetailsToDto(component)).pipe(map$(componentDetailsFromDto));
    } else {
      return this.componentRest.create(componentDetailsToDto(component)).pipe(map$(componentDetailsFromDto));
    }
  }

  saveAndPropagateFormula(component: ComponentDetails): Observable<ComponentDetails> {
    return this.save(component).pipe(switchMap$(
      (updatedComponent) => this.bulkUpdateRest.propagateFormula({
        oldFormula: component.oldFormula,
        newFormula: updatedComponent.formula,
        componentId: updatedComponent.id,
      }).pipe(map$(() => updatedComponent))));
  }

  recalculate(component: ComponentDetails): Observable<ComponentDetails> {
    return this.componentRest.recalculate(componentDetailsToDto(component)).pipe(map$(componentDetailsFromDto));
  }

  loadCostCenters(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.costCenterRest
      .findAllAutocompleteItemsAsDataSource({ searchString: query.filter, showDeleted: false }).select(query).pipe(
        map$(getSlicedData),
        map$((items) => items.map(withDisplayAsNameAndDescription)));
  }
  getMatchingComponentCodes(searchString: string): Observable<string[]> {
    return this.componentRest.getComponentCodes(searchString);
  }
}
